import { forwardRef, type MouseEvent, type MutableRefObject, useRef } from 'react'
import validator from '@rjsf/validator-ajv8'
import { useFetchSchema } from 'src/common/hooks'
import { type FormGeneratorProps } from 'src/FormGenerator'
import { JSONForm } from 'src/FormGenerator.styled'
import { READ_ONLY_WIDGETS, WIDGETS } from 'src/widgets'
import { type RJSFSchema } from '@rjsf/utils'
import { assignHorizontalTemplate, replacePlaceholders } from 'src/utils'
import { Skeleton } from '@mui/material'
import ErrorMessage from './ErrorMessage'
import styled from '@emotion/styled'
import transformErrors from 'src/utils/transform-errors'
import { type UiSchema } from 'src/utils/assign-horizontal-template'
import {
	ArrayFieldItemTemplate,
	ArrayTemplate,
	ArrayTemplateReadOnly,
	HorizontalTemplate,
	ObjectTemplateReadOnly
} from 'src/templates'
import type Form from '@rjsf/core'

const Button = styled.button`
	display: none;
`

const READ_ONLY_TEMPLATES = {
	HorizontalTemplate: (props: any) => <HorizontalTemplate {...props} readonly />,
	ObjectFieldTemplate: ObjectTemplateReadOnly,
	ArrayFieldTemplate: ArrayTemplateReadOnly
}

const TEMPLATES = {
	ArrayFieldTemplate: ArrayTemplate,
	ArrayFieldItemTemplate
}

type FormWrapperProps = Omit<FormGeneratorProps, 'onGetSchemas' | 'baseUrl' | 'httpOptions'>

const FormWrapper = forwardRef<Form | null, FormWrapperProps>((props, ref) => {
	const {
		schemaName,
		schema,
		uiSchema,
		placeholders,
		submitRef,
		onSubmit,
		readonly,
		tagName = 'form',
		...restProps
	} = props

	const formRef = useRef<Form | null>(null)
	const { data, error, isLoading } = useFetchSchema<{
		structure: RJSFSchema
		ui: any
		version: string
	}>(schemaName)

	const finalRef = (ref as MutableRefObject<Form | null>) ?? formRef

	if (error) return <ErrorMessage message='An error occurred while getting the form' />

	if (isLoading) return <Skeleton />

	const formattedSchemas = (() => {
		if (!placeholders && data) {
			return {
				schema: data.structure,
				uiSchema: assignHorizontalTemplate(data.ui, !!readonly)
			}
		}

		if (data && placeholders) {
			return {
				uiSchema: assignHorizontalTemplate(replacePlaceholders(data.ui, placeholders), !!readonly),
				schema: replacePlaceholders(data.structure, placeholders)
			}
		} else if (uiSchema && schema && placeholders) {
			return {
				uiSchema: assignHorizontalTemplate(replacePlaceholders(uiSchema, placeholders), !!readonly),
				schema: replacePlaceholders(schema, placeholders)
			}
		}

		if (schema && uiSchema) {
			return {
				schema,
				uiSchema: assignHorizontalTemplate(uiSchema as UiSchema, !!readonly)
			}
		}

		return {
			schema: null,
			uiSchema: null
		}
	})()

	if (!formattedSchemas.schema || !formattedSchemas.uiSchema) {
		return <ErrorMessage message='An error occurred while getting the form' />
	}

	const customSubmitHandler = (e: MouseEvent<HTMLButtonElement>) => {
		e.preventDefault()
		if (finalRef.current) {
			const isFormValid = finalRef.current.validateForm()
			if (isFormValid) {
				onSubmit?.(finalRef.current.state.formData, e)
			} else {
				console.error('Form validation failed', finalRef.current.state.errors)
			}
		}
	}

	return (
		<JSONForm
			ref={finalRef}
			schema={formattedSchemas.schema}
			validator={validator}
			uiSchema={formattedSchemas.uiSchema}
			widgets={readonly ? READ_ONLY_WIDGETS : WIDGETS}
			templates={readonly ? READ_ONLY_TEMPLATES : TEMPLATES}
			onSubmit={onSubmit}
			readOnly={readonly}
			transformErrors={transformErrors}
			showErrorList={false}
			tagName={tagName}
			{...restProps}
		>
			{tagName === 'form' && (
				<Button type='submit' ref={submitRef}>
					Submit
				</Button>
			)}
			{tagName !== 'form' && (
				<Button type='button' ref={submitRef} onClick={customSubmitHandler}>
					Submit
				</Button>
			)}
		</JSONForm>
	)
})

FormWrapper.displayName = 'FormWrapper'

export default FormWrapper
